/**
 * A Meta ad click arrives as ?fbclid=… on the app URL (the marketing site's
 * CTA links pass it through, so it works without any cookie consent) and must
 * survive until the visitor's first sign-in — across email confirmation and
 * the OAuth redirect — so it lives in localStorage as a ready-made fbc value
 * (`fb.1.<unix ms>.<fbclid>`). Meta's click attribution window is 7 days.
 * When the visitor accepted cookies on the marketing site the pixel has
 * already set `_fbc` on the shared parent domain; that is the fallback.
 */
const PENDING_META_CLICK_KEY = "pendingMetaClick";

const ATTRIBUTION_WINDOW_DAYS = 7;

const FBCLID_PATTERN = /^[A-Za-z0-9_-]{8,400}$/;

const FBC_PATTERN = /^fb\.1\.\d{10,13}\.[A-Za-z0-9_-]{8,400}$/;

interface StoredMetaClick {
  clickId: string;
  storedAt: string;
}

/** Stashes a valid ?fbclid= from the given query string as an fbc value; ignores everything else. */
export const captureMetaClickFromUrl = (search: string): void => {
  const fbclid = new URLSearchParams(search).get("fbclid");
  if (!fbclid || !FBCLID_PATTERN.test(fbclid)) {
    return;
  }
  try {
    const stored: StoredMetaClick = {
      clickId: `fb.1.${Date.now()}.${fbclid}`,
      storedAt: new Date().toISOString(),
    };
    localStorage.setItem(PENDING_META_CLICK_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode etc.) — the click is simply not tracked.
  }
};

/**
 * The stashed click id, else the pixel's own `_fbc` cookie, else null when
 * neither exists or the attribution window passed.
 */
export const getPendingMetaClick = (): string | null => readStoredClick() ?? readFbcCookie();

/**
 * The raw `fbclid` inside the stashed click id, for server-side flows that build
 * the fbc value themselves (the email-only trial start stores it with the account).
 */
export const getPendingMetaClickId = (): string | null => {
  const fbc = getPendingMetaClick();
  if (!fbc) {
    return null;
  }
  const fbclid = fbc.split(".").slice(3).join(".");
  return FBCLID_PATTERN.test(fbclid) ? fbclid : null;
};

export const clearPendingMetaClick = (): void => {
  try {
    localStorage.removeItem(PENDING_META_CLICK_KEY);
  } catch {
    // Nothing to clean up when storage is unavailable.
  }
};

const readStoredClick = (): string | null => {
  try {
    const raw = localStorage.getItem(PENDING_META_CLICK_KEY);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as StoredMetaClick;
    if (!stored.clickId || !FBC_PATTERN.test(stored.clickId)) {
      return null;
    }
    const ageMs = Date.now() - new Date(stored.storedAt).getTime();
    if (!Number.isFinite(ageMs) || ageMs > ATTRIBUTION_WINDOW_DAYS * 24 * 60 * 60 * 1000) {
      clearPendingMetaClick();
      return null;
    }
    return stored.clickId;
  } catch {
    return null;
  }
};

const readFbcCookie = (): string | null => {
  const match = document.cookie.match(/(?:^|;\s*)_fbc=([^;]+)/);
  const value = match ? decodeURIComponent(match[1]) : null;
  return value && FBC_PATTERN.test(value) ? value : null;
};
